import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Search,
  Calendar,
  ExternalLink,
  X,
  Save,
  RefreshCw,
} from 'lucide-react';
import { notifyError, notifySuccess } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted', tone: 'bg-[#E8A830]/15 text-[#8A6310] border-[#E8A830]/40' },
  { value: 'pending_payment', label: 'Pending payment', tone: 'bg-[#B91C1C]/10 text-[#7F1414] border-[#B91C1C]/40' },
  { value: 'scheduled', label: 'Scheduled', tone: 'bg-[#2CA8A0]/15 text-[#1A5C6E] border-[#2CA8A0]/40' },
  { value: 'completed', label: 'Completed', tone: 'bg-[#0A1F2E]/10 text-[#0A1F2E] border-[#0A1F2E]/30' },
  { value: 'cancelled', label: 'Cancelled', tone: 'bg-[#0A1F2E]/5 text-[#0A1F2E]/50 border-[#0A1F2E]/15' },
];

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'});
  } catch { return iso; }
};

const fmtDay = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch { return iso; }
};

const IntakeDrawer = ({ intake, onClose, onSaved }) => {
  const [status, setStatus] = useState(intake.status || 'submitted');
  const [scheduledAt, setScheduledAt] = useState(
    intake.scheduled_at ? intake.scheduled_at.slice(0, 16) : ''
  );
  const [notes, setNotes] = useState(intake.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/coaching/intakes/${intake.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : '',
          admin_notes: notes,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        notifySuccess('Intake updated.');
        onSaved(d.intake);
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not save.');
      }
    } catch {
      notifyError('Network error.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-coaching-drawer">
      <div className="absolute inset-0 bg-[#0A1F2E]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white border-l border-[#EFE6CF] h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-[#EFE6CF] flex items-center justify-between p-5">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">
              {intake.type === 'additional_paid' ? '$99 additional call' : 'Included call'}
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E]">{intake.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#0A1F2E]/40 hover:text-[#0A1F2E]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <section className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Email" value={intake.user_email || '-'} />
            <Info label="Destination" value={intake.destination_city} />
            <Info label="Timeline" value={intake.timeline} />
            <Info label="Submitted" value={fmtDate(intake.created_at)} />
            <Info
              label="Type"
              value={intake.type === 'additional_paid' ? 'Paid $99' : 'Included in membership'}
            />
            <Info
              label="Stripe session"
              value={
                intake.stripe_session_id ? (
                  <span className="font-mono text-xs break-all">{intake.stripe_session_id}</span>
                ) : '-'
              }
            />
          </section>

          <section>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-2">
              Top questions
            </p>
            <div className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-lg p-4 text-sm text-[#0A1F2E] whitespace-pre-wrap">
              {intake.top_questions}
            </div>
          </section>

          <section className="space-y-4">
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">
              Admin actions
            </p>

            <div>
              <label className="block text-xs text-[#0A1F2E]/65 mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
                data-testid="admin-coaching-status-select"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-[#0A1F2E]/65 mb-1.5">Scheduled at</label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
                data-testid="admin-coaching-scheduled-input"
              />
            </div>

            <div>
              <label className="block text-xs text-[#0A1F2E]/65 mb-1.5">Admin notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Prep notes, follow-ups, links shared after the call…"
                className="w-full bg-white border border-[#EFE6CF] rounded-lg px-3 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
                data-testid="admin-coaching-notes-input"
              />
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition disabled:opacity-60"
              data-testid="admin-coaching-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-0.5">{label}</p>
    <p className="text-sm text-[#0A1F2E]">{value}</p>
  </div>
);

const AdminCoaching = () => {
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [active, setActive] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/coaching/intakes?limit=500`, {
        credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        setIntakes(d.intakes || []);
      } else if (r.status === 403 || r.status === 401) {
        notifyError('Admin session expired. Please log in again.');
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not load coaching intakes.');
      }
    } catch {
      notifyError('Network error loading intakes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = intakes.filter((i) => {
      if (statusFilter !== 'all' && (i.status || 'submitted') !== statusFilter) return false;
      if (typeFilter !== 'all' && (i.type || 'included') !== typeFilter) return false;
      if (!q) return true;
      return (
        (i.name || '').toLowerCase().includes(q) ||
        (i.user_email || '').toLowerCase().includes(q) ||
        (i.destination_city || '').toLowerCase().includes(q) ||
        (i.top_questions || '').toLowerCase().includes(q)
      );
    });
    rows = rows.sort((a, b) => {
      switch (sortBy) {
        case 'created_at_asc':
          return (a.created_at || '').localeCompare(b.created_at || '');
        case 'scheduled_at_asc':
          return (a.scheduled_at || '\uffff').localeCompare(b.scheduled_at || '\uffff');
        case 'scheduled_at_desc':
          return (b.scheduled_at || '').localeCompare(a.scheduled_at || '');
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'created_at_desc':
        default:
          return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });
    return rows;
  }, [intakes, search, statusFilter, typeFilter, sortBy]);

  const stats = useMemo(() => ({
    total: intakes.length,
    pending: intakes.filter((i) => (i.status || 'submitted') === 'submitted').length,
    scheduled: intakes.filter((i) => i.status === 'scheduled').length,
    paid: intakes.filter((i) => i.type === 'additional_paid').length,
  }), [intakes]);

  const handleSaved = (updated) => {
    setIntakes((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    setActive(updated);
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]" data-testid="admin-coaching-page">
      <header className="border-b border-[#EFE6CF] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] transition" data-testid="admin-coaching-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Mundalo · Admin</p>
            <h1 className="font-display italic text-2xl text-[#0A1F2E]">Coaching Calls</h1>
          </div>
          <button
            type="button"
            onClick={reload}
            className="ml-auto inline-flex items-center gap-2 text-[#1A5C6E] hover:text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full border border-[#EFE6CF] hover:border-[#0A1F2E]/30 transition"
            data-testid="admin-coaching-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total intakes" value={stats.total} />
          <StatCard label="Awaiting prep" value={stats.pending} />
          <StatCard label="Scheduled" value={stats.scheduled} />
          <StatCard label="Paid $99 calls" value={stats.paid} />
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#EFE6CF] rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, destination, question…"
              className="w-full bg-[#F9F6EF] border border-[#EFE6CF] rounded-full pl-10 pr-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
              data-testid="admin-coaching-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid="admin-coaching-filter-status"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid="admin-coaching-filter-type"
          >
            <option value="all">All types</option>
            <option value="included">Included</option>
            <option value="additional_paid">Paid $99</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid="admin-coaching-sort"
          >
            <option value="created_at_desc">Newest first</option>
            <option value="created_at_asc">Oldest first</option>
            <option value="scheduled_at_asc">Soonest scheduled</option>
            <option value="scheduled_at_desc">Latest scheduled</option>
            <option value="name_asc">Name (A→Z)</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20 text-[#0A1F2E]/55">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-20 text-[#0A1F2E]/55" data-testid="admin-coaching-empty">
            {intakes.length === 0
              ? 'No coaching intakes yet. They’ll show up here as soon as members submit the Schedule a Call form.'
              : 'No intakes match these filters.'}
          </p>
        ) : (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-coaching-table">
              <thead>
                <tr className="text-left font-mono uppercase text-[10px] tracking-[0.12em] text-[#0A1F2E]/55 border-b border-[#EFE6CF]">
                  <th className="py-3 px-4">Submitted</th>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Destination</th>
                  <th className="py-3 px-4">Timeline</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Scheduled</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => {
                  const meta = statusMeta(i.status || 'submitted');
                  const isPaid = i.type === 'additional_paid';
                  return (
                    <tr
                      key={i.id}
                      className="border-b border-[#EFE6CF]/60 hover:bg-[#F9F6EF]/60 transition cursor-pointer"
                      onClick={() => setActive(i)}
                      data-testid={`admin-coaching-row-${i.id}`}
                    >
                      <td className="py-3 px-4 text-[#0A1F2E]/70 whitespace-nowrap">{fmtDay(i.created_at)}</td>
                      <td className="py-3 px-4 text-[#0A1F2E]">
                        <div className="font-medium">{i.name}</div>
                        <div className="text-xs text-[#0A1F2E]/55">{i.user_email}</div>
                      </td>
                      <td className="py-3 px-4 text-[#0A1F2E]/80">{i.destination_city}</td>
                      <td className="py-3 px-4 text-[#0A1F2E]/70">{i.timeline}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-1 rounded-full border ${
                          isPaid
                            ? 'bg-[#E8A830]/15 text-[#8A6310] border-[#E8A830]/40'
                            : 'bg-[#2CA8A0]/10 text-[#1A5C6E] border-[#2CA8A0]/30'
                        }`}>
                          {isPaid ? 'Paid $99' : 'Included'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-1 rounded-full border ${meta.tone}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-[#0A1F2E]/70 whitespace-nowrap">
                        {i.scheduled_at ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {fmtDate(i.scheduled_at)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setActive(i); }}
                          className="text-[#1A5C6E] hover:text-[#0A1F2E] inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.1em]"
                          data-testid={`admin-coaching-open-${i.id}`}
                        >
                          Open <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {active && (
        <IntakeDrawer
          intake={active}
          onClose={() => setActive(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-[#EFE6CF] rounded-2xl px-4 py-3" data-testid={`admin-coaching-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">{label}</p>
    <p className="font-display text-3xl text-[#0A1F2E] mt-1">{value}</p>
  </div>
);

export default AdminCoaching;
